import { useEffect, useRef, useState } from 'react';
import {
  OrientationLockType,
  ScreenOrientation,
} from '@capacitor/screen-orientation';

type OrientationChangeHandler = (orientation: OrientationLockType) => void;

export const useDeviceOrientation = (
  /**
   * Invoked synchronously from the native listener, before the returned value
   * updates. Must stay cheap: it runs in the event path, ahead of the render.
   *
   * Emission timing is platform-dependent. iOS reports device orientation, so it
   * fires before the WebView updates its layout; Android and web fire once the new
   * configuration is already applied.
   */
  onNativeOrientationChange?: OrientationChangeHandler,
): OrientationLockType => {
  const [orientation, setOrientation] = useState<OrientationLockType>('any');
  const isInitialOrientationSet = useRef(false);
  const onNativeOrientationChangeRef = useRef(onNativeOrientationChange);
  onNativeOrientationChangeRef.current = onNativeOrientationChange;

  useEffect(() => {
    const getCurrentOrientation = async (): Promise<void> => {
      if (isInitialOrientationSet.current) return;
      try {
        const { type } = await ScreenOrientation.orientation();
        if (isInitialOrientationSet.current) return;
        setOrientation(type);
        isInitialOrientationSet.current = true;
      } catch {
        // The ScreenOrientation API is unavailable in some old browsers. Swallow it
        // and keep the default orientation: consumers that only react to changes simply
        // stay put, which is the desired no-op rather than an unhandled rejection.
      }
    };

    const listenerHandle = ScreenOrientation.addListener(
      'screenOrientationChange',
      ({ type }) => {
        onNativeOrientationChangeRef.current?.(type);
        setOrientation(type);
        isInitialOrientationSet.current = true;
      },
    );

    getCurrentOrientation();

    return () => {
      // Remove only this instance's listener, leaving other consumers' intact.
      listenerHandle.then((handle) => handle.remove()).catch(() => {});
    };
  }, []);

  return orientation;
};
